import React from 'react';
import Icon from '../../../components/AppIcon';

const BulkProcessingLog = ({ logs, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" className="animate-spin h-8 w-8 text-primary" />
      </div>
    );
  }

  if (!logs || logs?.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="Activity" size={48} className="mx-auto mb-4 text-muted-foreground" />
        <p className="text-muted-foreground">No processing logs available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {logs?.map((log) => {
        const successRate = log?.totalRecords > 0 
          ? ((log?.successfulRecords / log?.totalRecords) * 100)?.toFixed(1)
          : 0;
        const isComplete = log?.completedAt !== null;
        const hasErrors = log?.failedRecords > 0;

        return (
          <div key={log?.id} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Icon 
                    name={isComplete ? (hasErrors ? 'AlertTriangle' : 'CheckCircle') : 'Loader2'} 
                    size={18} 
                    className={`${
                      isComplete 
                        ? (hasErrors ? 'text-warning' : 'text-success')
                        : 'text-primary animate-spin'
                    }`}
                  />
                  <h4 className="font-semibold text-foreground">{log?.processingType}</h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  Started: {new Date(log?.startedAt)?.toLocaleString()}
                </p>
                {log?.completedAt && (
                  <p className="text-sm text-muted-foreground">
                    Completed: {new Date(log?.completedAt)?.toLocaleString()}
                  </p>
                )}
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-foreground">{successRate}%</div>
                <div className="text-xs text-muted-foreground">Success Rate</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 mb-3">
              <div className="bg-muted rounded p-3">
                <p className="text-xs text-muted-foreground mb-1">Total Records</p>
                <p className="text-lg font-bold text-foreground">{log?.totalRecords}</p>
              </div>
              <div className="bg-success/10 rounded p-3">
                <p className="text-xs text-muted-foreground mb-1">Successful</p>
                <p className="text-lg font-bold text-success">{log?.successfulRecords}</p>
              </div>
              <div className="bg-error/10 rounded p-3">
                <p className="text-xs text-muted-foreground mb-1">Failed</p>
                <p className="text-lg font-bold text-error">{log?.failedRecords}</p>
              </div>
            </div>
            {log?.departments?.length > 0 && (
              <div className="mb-3">
                <p className="text-xs text-muted-foreground mb-2">Departments:</p>
                <div className="flex flex-wrap gap-2">
                  {log?.departments?.map((dept) => (
                    <span key={dept} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded">
                      {dept}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {log?.errorDetails && log?.errorDetails?.length > 0 && (
              <div className="bg-error/10 border border-error rounded p-3">
                <p className="text-sm font-medium text-error mb-2">
                  <Icon name="AlertTriangle" size={14} className="inline mr-1" />
                  Errors Encountered:
                </p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {log?.errorDetails?.slice(0, 5)?.map((err, idx) => (
                    <p key={idx} className="text-xs text-muted-foreground">
                      • {err?.error || 'Unknown error'}
                    </p>
                  ))}
                  {log?.errorDetails?.length > 5 && (
                    <p className="text-xs text-muted-foreground italic">
                      ... and {log?.errorDetails?.length - 5} more errors
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default BulkProcessingLog;